import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import {
  spinWheelApi,
  userdetailApi,
  userTotalPointsApi,
} from "../../http/http";
import { isFresh } from "../cacheUtils";

const DETAILS_TTL_MS = 1000 * 60 * 10; // 10 min
const POINTS_TTL_MS = 1000 * 60 * 5; // 5 min
const SPIN_TTL_MS = 1000 * 60 * 10; // 10 min

export const fetchUserDetails = createAsyncThunk(
  "user/fetchUserDetails",
  async (token) => {
    if (!token) throw new Error("Missing auth token");
    const res = await userdetailApi(token);
    return res?.data?.[0] || null;
  },
  {
    condition: (token, { getState }) => {
      const state = getState().user;
      if (!token) return false;
      if (state.token && state.token !== token) return true;
      return !isFresh(state.detailsLastFetched, DETAILS_TTL_MS);
    },
  }
);

export const fetchUserTotalPoints = createAsyncThunk(
  "user/fetchUserTotalPoints",
  async (token) => {
    if (!token) throw new Error("Missing auth token");
    const res = await userTotalPointsApi(token);
    return res?.data?.totalPoints || 0;
  },
  {
    condition: (token, { getState }) => {
      const state = getState().user;
      if (!token) return false;
      if (state.token && state.token !== token) return true;
      return !isFresh(state.pointsLastFetched, POINTS_TTL_MS);
    },
  }
);

export const fetchSpinStatus = createAsyncThunk(
  "user/fetchSpinStatus",
  async (token) => {
    if (!token) throw new Error("Missing auth token");
    const res = await spinWheelApi(token);
    return res?.data?.spinStatus || "";
  },
  {
    condition: (token, { getState }) => {
      const state = getState().user;
      if (!token) return false;
      if (state.token && state.token !== token) return true;
      return !isFresh(state.spinLastFetched, SPIN_TTL_MS);
    },
  }
);

const userSlice = createSlice({
  name: "user",
  initialState: {
    token: null,

    details: null,
    totalPoints: 0,
    spinStatus: "",

    detailsStatus: "idle",
    pointsStatus: "idle",
    spinStatusStatus: "idle",

    error: null,

    detailsLastFetched: null,
    pointsLastFetched: null,
    spinLastFetched: null,
  },
  reducers: {
    clearUser(state) {
      state.token = null;
      state.details = null;
      state.totalPoints = 0;
      state.spinStatus = "";
      state.detailsStatus = "idle";
      state.pointsStatus = "idle";
      state.spinStatusStatus = "idle";
      state.error = null;
      state.detailsLastFetched = null;
      state.pointsLastFetched = null;
      state.spinLastFetched = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserDetails.pending, (state) => {
        state.detailsStatus = "loading";
        state.error = null;
      })
      .addCase(fetchUserDetails.fulfilled, (state, action) => {
        state.detailsStatus = "succeeded";
        state.details = action.payload;
        state.token = action.meta.arg;
        state.detailsLastFetched = Date.now();
      })
      .addCase(fetchUserDetails.rejected, (state, action) => {
        state.detailsStatus = "failed";
        state.error = action.error?.message || "Failed to load user details";
      })
      .addCase(fetchUserTotalPoints.pending, (state) => {
        state.pointsStatus = "loading";
        state.error = null;
      })
      .addCase(fetchUserTotalPoints.fulfilled, (state, action) => {
        state.pointsStatus = "succeeded";
        state.totalPoints = action.payload;
        state.token = state.token || action.meta.arg;
        state.pointsLastFetched = Date.now();
      })
      .addCase(fetchUserTotalPoints.rejected, (state, action) => {
        state.pointsStatus = "failed";
        state.error = action.error?.message || "Failed to load user points";
      })
      .addCase(fetchSpinStatus.pending, (state) => {
        state.spinStatusStatus = "loading";
        state.error = null;
      })
      .addCase(fetchSpinStatus.fulfilled, (state, action) => {
        state.spinStatusStatus = "succeeded";
        state.spinStatus = action.payload;
        state.token = state.token || action.meta.arg;
        state.spinLastFetched = Date.now();
      })
      .addCase(fetchSpinStatus.rejected, (state, action) => {
        state.spinStatusStatus = "failed";
        state.error = action.error?.message || "Failed to load spin status";
      });
  },
});

export const { clearUser } = userSlice.actions;
export default userSlice.reducer;

